import { Stack } from '@mui/material';
import moment from 'moment-timezone';
import React, { HTMLAttributes } from 'react';

import { Tooltip } from '@sumologic/yama-ds-components';

import { Indicator } from 'app/Entities/Indicator';
import { ContextActionContainer } from 'shared/components/internal/ContextAction';
import {
  RecordType,
  recordTypes,
  rendererMap,
} from 'shared/components/internal/Records';
import { type EntityTypeFieldsAndIndicator } from 'shared/components/internal/Records/useEntityTypeRecordFields';

const useContent = (
  name: string,
  children: React.ReactNode,
  showTooltipWithValue: boolean,
  record?: RecordType
) => {
  if (
    typeof children === 'object' &&
    (children as object)?.hasOwnProperty('props')
  ) {
    // If child is a React element then bail out, renderers will crash
    return children;
  }

  try {
    const type = recordTypes?.[name];
    const renderer = rendererMap?.[type];
    const renderedContent = renderer?.(children, record) ?? children;

    return showTooltipWithValue && renderedContent ? (
      <Tooltip
        title={renderedContent}
        componentsProps={{
          tooltip: {
            sx: {
              maxWidth: 300,
              wordBreak: 'break-all',
            },
          },
        }}
      >
        <span>{renderedContent}</span>
      </Tooltip>
    ) : (
      renderedContent
    );
  } catch (error) {
    return 'Unable to show value';
  }
};

interface RecordValueProps extends HTMLAttributes<HTMLSpanElement> {
  hideDots?: boolean;
  name: string;
  record?: RecordType;
  showContextAction?: boolean;
  showTooltipWithValue?: boolean;
  entityIndicatorData?: EntityTypeFieldsAndIndicator['entityIndicatorData'];
}

export const RecordValue = ({
  children,
  hideDots = false,
  name,
  record,
  showContextAction = true,
  showTooltipWithValue = false,
  entityIndicatorData,
  ...rest
}: RecordValueProps) => {
  const content = useContent(name, children, showTooltipWithValue, record);

  // Timestamp is expected to be in UNIX format in milliseconds.
  return (
    <Stack direction='row' alignItems='center'>
      <span {...rest}>
        {showContextAction ? (
          <ContextActionContainer
            contextData={record}
            eventTimestamp={
              record ? moment.unix(record.timestamp / 1000).toDate() : undefined
            }
            hideDots={hideDots}
            hoverMatch={true}
            matchers={[{ type: 'recordProperty' }]}
            recordField={name}
          >
            {content}
          </ContextActionContainer>
        ) : (
          content
        )}
      </span>
      {entityIndicatorData && <Indicator entity={entityIndicatorData} />}
    </Stack>
  );
};
